import { messageTemplateNames } from "@lib/constant";
import { parseMessageTemplate } from "@lib/practice/helper";
import { renderPaymentRequesetEmailTemplate } from "@lib/templates/paymentRequest/PaymentRequesetEmailTemplate";

export const PAYMENT_REQUEST_VARIABLES = [
  { name: "patientName", description: "Patient Full Name" },
  { name: "firstName", description: "Patient First Name" },
  { name: "officeName", description: "Office Name" },
  { name: "officePhone", description: "Office Phone Number" },
  { name: "officeEmail", description: "Office Email" },
  { name: "amount", description: "Balance Amount" },
  { name: "paymentLink", description: "Payment Link" },
];

export type PRSubTemplate = {
  templateName: string;
  label: string;
  subject: string;
  content: string;
};

export const PR_SUB_TEMPLATES: PRSubTemplate[] = [
  {
    templateName: messageTemplateNames.PAYMENT_REQUEST_BASIC,
    label: "Basic Payment Request",
    subject: "Your Payment is Due\u2014Please Review Your Bill",
    content:
      "Hi {patientName},\n\nThis is a reminder that you have a balance of ${amount} with {officeName}. Please click the link below to review your statement and make a payment:\n\n{paymentLink}\n\nIf you\u2019ve already paid this balance, please disregard this statement. If you have any questions, please contact us at {officeEmail}.\n\nThank you,\n{officeName}",
  },
  {
    templateName: messageTemplateNames.PAYMENT_REQUEST_REMINDER,
    label: "Friendly Reminder",
    subject: "Quick Reminder: Your {officeName} Bill is Ready",
    content:
      "Hi {patientName},\n\nWe hope this message finds you well! It looks like there\u2019s a balance of ${amount} on your account with {officeName}. You can review your bill and make a payment by clicking the link below:\n\n{paymentLink}\n\nIf you\u2019ve already paid this balance, please disregard this statement. If you have any questions, please contact us at {officeEmail}.\n\nThank you, and have a wonderful day!",
  },
  {
    templateName: messageTemplateNames.PAYMENT_REQUEST_FOLLOWUP,
    label: "Professional Follow-Up",
    subject: "Payment Reminder: View Your {officeName} Statement",
    content:
      "Hi {patientName},\n\nThis is a courtesy reminder regarding your outstanding balance of ${amount} with {officeName}. You can easily review your bill and submit your payment using the link below:\n\n{paymentLink}\n\nIf this payment has already been made or you think this message was sent in error, no further action is needed. If you have any questions, please contact us at {officeEmail}.\n\nThank you for your attention to this matter.",
  },
  {
    templateName: messageTemplateNames.PAYMENT_REQUEST_URGENT,
    label: "Urgent Payment Request",
    subject: "Action Needed: Outstanding Balance with {officeName}",
    content:
      "Hi {patientName},\n\nWe are reaching out regarding your overdue balance of ${amount} with {officeName}. Please use the link below to view your statement and submit payment as soon as possible:\n\n{paymentLink}\n\nIf you\u2019ve recently made this payment, thank you! Otherwise, please address this matter at your earliest convenience to avoid any further action. If you have any questions, please contact us at {officeEmail}.\n\nThank you,\n{officeName}",
  },
  {
    templateName: messageTemplateNames.PAYMENT_REQUEST_FINAL,
    label: "Final Notice",
    subject: "Final Notice: Immediate Payment Required for Your Account",
    content:
      "Hi {patientName},\n\nThis is a final notice regarding your unpaid balance of ${amount} with {officeName}. Immediate payment is required to prevent further escalation of this matter. Please review your bill and settle your account using the link below:\n\n{paymentLink}\n\nIf this payment has already been made, please disregard this message. Otherwise, prompt action is needed. If you have any questions, please contact us at {officeEmail}.\n\nThank you,\n{officeName}",
  },
  {
    templateName: messageTemplateNames.PAYMENT_REQUEST_ENCOURAGEMENT,
    label: "Friendly Encouragement",
    subject: "Let\u2019s Get This Taken Care Of\u2014View Your {officeName} Bill Today",
    content:
      "Hi {patientName},\n\nWe wanted to remind you about your balance of ${amount} with {officeName}. Taking care of it is quick and easy \u2013 just click the link below to view your bill and make a payment:\n\n{paymentLink}\n\nIf you\u2019ve already paid this balance, please disregard this statement. If you have any questions, please contact us at {officeEmail}.\n\nThank you,\n{officeName}",
  },
  {
    templateName: messageTemplateNames.PAYMENT_REQUEST_TEMPLATE_1,
    label: "Insurance Claims Processed",
    subject: "Your Dental Insurance Claims Have Been Processed - Remaining Balance",
    content:
      "Hello,\n\nWe wanted to let you know that all of your dental insurance claims have now been fully processed and closed. After insurance payments were applied, there is a remaining balance on your account of ${amount}.\n\nYou may conveniently pay your balance by clicking the secure link below:\n\n{paymentLink}\n\nIf you have any questions about your balance or need to set up a payment plan, please don\u2019t hesitate to contact our office at {officePhone}.\n\nThank you,\n{officeName}",
  },
  {
    templateName: messageTemplateNames.PAYMENT_REQUEST_CUSTOM,
    label: "Custom Template",
    subject: "Payment Request from {officeName}",
    content:
      "Hi {patientName},\n\nPlease pay your balance of ${amount} at your earliest convenience.\n\nThank you!",
  },
];

export const PR_TEMPLATE_VALUE_TO_NAME: Record<string, string> = {
  basic: messageTemplateNames.PAYMENT_REQUEST_BASIC,
  reminder: messageTemplateNames.PAYMENT_REQUEST_REMINDER,
  "follow-up": messageTemplateNames.PAYMENT_REQUEST_FOLLOWUP,
  urgent: messageTemplateNames.PAYMENT_REQUEST_URGENT,
  final: messageTemplateNames.PAYMENT_REQUEST_FINAL,
  encourage: messageTemplateNames.PAYMENT_REQUEST_ENCOURAGEMENT,
  "template-1": messageTemplateNames.PAYMENT_REQUEST_TEMPLATE_1,
  custom: messageTemplateNames.PAYMENT_REQUEST_CUSTOM,
};

export const PRTemplateOptions = PR_SUB_TEMPLATES.map((t) => ({
  value: t.templateName,
  label: t.label,
}));

export const getDefaultPRTemplate = (templateName: string): PRSubTemplate | undefined => {
  return PR_SUB_TEMPLATES.find((t) => t.templateName === templateName);
};

export const getPaymentRequestTemplate = async (props: {
  officeName: string;
  officePhone: string;
  officeAddress: string;
  headerColor: string;
  officeLogo: string;
  template: {
    subject: string;
    content: string;
  };
  sourceInfo: Record<string, string | undefined | null>;
}) => {
  const { officeName, officePhone, officeAddress, headerColor, officeLogo, template, sourceInfo } = props;
  const defaultContent = parseMessageTemplate(template.content, sourceInfo);
  const defaultSubject = parseMessageTemplate(template.subject, sourceInfo);
  const emailContent = defaultContent.replaceAll("\n", "<br />");

  const html = await renderPaymentRequesetEmailTemplate({
    officeName,
    officePhone,
    officeAddress,
    headerColor,
    officeLogo,
    content: emailContent,
  });
  return {
    emailTemplate: html,
    smsTemplate: defaultContent,
    subject: defaultSubject,
  };
};
